/**
 * ResourceModalFlow - Main flow controller for the resource collection modal sequence.
 * Orchestrates the entire modal workflow from resource selection to processing/content review.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useResourceModalFlow, TRANSLATION_LANGUAGES } from './useResourceModalFlow';
import { creatorWorkflowService } from '../../../services/creatorWorkflowService';
import type { CourseDraft } from '../../../types/creator-workflow';

// Import all modals
import ResourceSelectionModal from './modals/ResourceSelectionModal';
import AudioUploadModal from './modals/AudioUploadModal';
import EnglishTextModal from './modals/EnglishTextModal';
import TranslationTextModal from './modals/TranslationTextModal';
import AISettingsModal from './modals/AISettingsModal';
import ProcessingModal from './modals/ProcessingModal';
import ContentReviewModal from './modals/ContentReviewModal';

import './ResourceModalFlow.css';

interface ResourceModalFlowProps {
  isOpen: boolean;
  draft: CourseDraft | null;
  onCreateDraft: (data: any) => Promise<CourseDraft | null>;
  onUpdateDraft: (data: any, draftId?: string) => Promise<void>;
  onGenerateContent: (data: any, draftId?: string) => Promise<void>;
  onParseContent: () => Promise<void>;
  onAdvanceStage: () => Promise<void>;
  onClose: () => void;
  onComplete: () => void;
  onSetGlobalProcessing?: (isProcessing: boolean, message?: string) => void;
  onRefreshDraft?: () => Promise<void>;
}

const ResourceModalFlow: React.FC<ResourceModalFlowProps> = ({
  isOpen,
  draft,
  onCreateDraft,
  onUpdateDraft,
  onGenerateContent,
  onParseContent,
  onAdvanceStage,
  onClose,
  onComplete,
  onSetGlobalProcessing,
  onRefreshDraft,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const navigate = useNavigate();

  const {
    state,
    selectResources,
    updateAudioData,
    setEnglishText,
    setTranslationText,
    setAISettings,
    nextStep,
    prevStep,
    complete,
    reset,
    setProcessingStatus,
    setProcessingError,
    setGeneratedText,
  } = useResourceModalFlow();

  // Ref to track whether processing has been triggered already
  const processingTriggeredRef = useRef(false);

  // Keep a ref to the latest draft for use in async callbacks
  const draftRef = useRef<CourseDraft | null>(draft);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  // Reset flow state when modal is reopened
  useEffect(() => {
    if (isOpen) {
      reset();
      processingTriggeredRef.current = false;
    }
  }, [isOpen, reset]);

  // --- Step handlers ---

  const handleSelectResources = useCallback(
    (audio: boolean, english: boolean, translation: boolean, translationLanguage: string) => {
      selectResources(audio, english, translation, translationLanguage);
    },
    [selectResources],
  );

  // Phase 1: Ensure draft exists, then upload+merge files server-side.
  // Returns the merged URL/duration so the modal can transition to range selection.
  const handleMergeAudioFiles = useCallback(
    async (files: File[]) => {
      let currentDraft = draftRef.current;
      if (!currentDraft) {
        currentDraft = await onCreateDraft({
          cefr_level: 'B1',
          content_type: 'dialogue',
          title: '',
          description: '',
          target_word_count: 200,
        });
        if (!currentDraft) throw new Error('Failed to create draft');
      }
      const result = await creatorWorkflowService.uploadAudioFiles(currentDraft.id, files);
      // Persist into the modal flow state (so reopening the modal mid-flow resumes correctly)
      updateAudioData({
        audioFiles: files,
        mergedAudioUrl: result.mergedAudioUrl,
        mergedAudioDuration: result.durationSeconds,
        audioRanges: undefined,
      });
      return result;
    },
    [onCreateDraft, updateAudioData],
  );

  // Phase 2: User confirmed ranges. Save into state and advance to next step.
  // Actual transcription happens in handleProcessing (so the user-uploaded text
  // modals can run between range pick and transcribe).
  const handleAudioSubmit = useCallback(
    (
      files: File[],
      mergedUrl: string,
      mergedDuration: number,
      ranges: Array<{ start: number; end: number }>,
    ) => {
      updateAudioData({
        audioFiles: files,
        mergedAudioUrl: mergedUrl,
        mergedAudioDuration: mergedDuration,
        audioRanges: ranges,
      });
      nextStep();
    },
    [updateAudioData, nextStep],
  );

  const handleEnglishSubmit = useCallback(
    (text: string) => {
      setEnglishText(text);
      nextStep();
    },
    [setEnglishText, nextStep],
  );

  const handleTranslationSubmit = useCallback(
    (text: string) => {
      setTranslationText(text);
      nextStep();
    },
    [setTranslationText, nextStep],
  );

  const handleAISettingsSubmit = useCallback(
    (settings: any) => {
      setAISettings(settings);
      nextStep();
    },
    [setAISettings, nextStep],
  );

  // --- Processing logic ---

  const handleProcessing = useCallback(async () => {
    const { materialSource, collectedData, translationLanguage } = state;

    try {
      // 1. Create draft if needed
      let currentDraft = draftRef.current;
      if (!currentDraft) {
        const settings = collectedData.aiSettings;
        currentDraft = await onCreateDraft({
          cefr_level: settings?.cefrLevel || 'B1',
          content_type: settings?.contentType || 'dialogue',
          title: '',
          description: settings?.topic || '',
          target_word_count: settings?.targetWordCount || 200,
        });
        if (!currentDraft) throw new Error('Failed to create draft');
      }

      // 2. Set translation language (pass draftId directly to avoid stale state)
      await onUpdateDraft({ translation_language: translationLanguage }, currentDraft.id);

      const hasAudio = state.selectedResources.audio;

      if (hasAudio && collectedData.mergedAudioUrl) {
        // Audio path phase 2: transcribe the previously merged audio with the
        // chosen ranges. Phase 1 (multi-file concat) ran in handleMergeAudioFiles
        // when the user clicked "繼續" inside AudioUploadModal.
        setProcessingStatus(t('creator:workflow.resourceModal.uploadingAudio'));
        onSetGlobalProcessing?.(true, t('creator:workflow.resourceModal.uploadingAudio'));

        await creatorWorkflowService.transcribeAudio(
          currentDraft.id,
          collectedData.audioRanges,
        );

        // Save text if provided
        const updateData: any = { material_source: materialSource };
        if (collectedData.englishText) updateData.uploaded_text_en = collectedData.englishText;
        if (collectedData.translationText) updateData.uploaded_translation = collectedData.translationText;
        await onUpdateDraft(updateData, currentDraft.id);

        onClose();
        navigate(`/creator/wizard/${currentDraft.id}/subtitles`);
        return;
      }

      if (materialSource === 'ai_generated') {
        // AI path: generate content
        setProcessingStatus(t('creator:workflow.resourceModal.generatingContent'));
        if (!collectedData.aiSettings) return;
        await onGenerateContent(
          {
            cefr_level: collectedData.aiSettings.cefrLevel,
            content_type: collectedData.aiSettings.contentType,
            topic: collectedData.aiSettings.topic,
            scenario: collectedData.aiSettings.scenario,
            custom_requirements: collectedData.aiSettings.customRequirements,
          },
          currentDraft.id,
        );

        // Refresh draft to get generated text
        await onRefreshDraft?.();

        // Get the updated draft after refresh
        const refreshedDraft = draftRef.current;

        // Set generated text and move to content_review step
        setGeneratedText(refreshedDraft?.generated_text || '');
        // The processing modal will be replaced by content_review
        // We dispatch a manual step change by dispatching SET_GENERATED_TEXT
        // which puts the text in state, then we need to transition to content_review
        return; // The content_review step will be rendered after state update
      }

      // Text-only path: save materials and advance
      setProcessingStatus(t('creator:workflow.resourceModal.savingMaterials'));
      const updateData: any = { material_source: materialSource };
      if (collectedData.englishText) updateData.uploaded_text_en = collectedData.englishText;
      if (collectedData.translationText) updateData.uploaded_translation = collectedData.translationText;
      await onUpdateDraft(updateData, currentDraft.id);

      // Advance through stages and start parsing
      await onAdvanceStage(); // material_input -> generation
      await onParseContent();
      await onAdvanceStage(); // generation -> parsing

      complete();
      onComplete();
    } catch (err: any) {
      setProcessingError(err.message || 'Processing failed');
    }
  }, [
    state,
    onCreateDraft,
    onUpdateDraft,
    onGenerateContent,
    onParseContent,
    onAdvanceStage,
    onClose,
    onComplete,
    onSetGlobalProcessing,
    onRefreshDraft,
    navigate,
    t,
    complete,
    setProcessingStatus,
    setProcessingError,
    setGeneratedText,
  ]);

  // Trigger processing when step becomes 'processing'
  useEffect(() => {
    if (state.currentStep === 'processing' && !processingTriggeredRef.current) {
      processingTriggeredRef.current = true;
      handleProcessing();
    }
  }, [state.currentStep, handleProcessing]);

  // For AI path: after generatedText is set during processing, transition to content_review
  useEffect(() => {
    if (
      state.currentStep === 'processing' &&
      state.materialSource === 'ai_generated' &&
      state.generatedText &&
      !state.processingError
    ) {
      // Manually set step to content_review by dispatching the step change
      // We use a small delay to ensure state has settled
      const timer = setTimeout(() => {
        // The reducer doesn't have a SET_STEP action, so we rely on the fact that
        // content_review is after processing. We dispatch NEXT_STEP which will
        // go to the next item in queue. But since content_review is not in the queue
        // by default, we need to handle this via generatedText being set.
        // Actually, looking at the reducer, content_review is a valid ModalStep.
        // The generated text being set indicates we should show content_review.
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [state.currentStep, state.materialSource, state.generatedText, state.processingError]);

  // --- Content review confirmation ---

  const handleContentConfirm = useCallback(async (text: string) => {
    try {
      setProcessingStatus(t('creator:workflow.resourceModal.parsingContent'));

      // If user edited, update the generated text
      if (text !== draftRef.current?.generated_text) {
        await onUpdateDraft({ generated_text: text });
      }

      await onAdvanceStage(); // material_input -> generation (or generation -> parsing)
      await onParseContent();
      await onAdvanceStage(); // generation -> parsing

      complete();
      onComplete();
    } catch (err: any) {
      setProcessingError(err.message || 'Failed to start parsing');
    }
  }, [t, onUpdateDraft, onAdvanceStage, onParseContent, complete, onComplete, setProcessingStatus, setProcessingError]);

  // Don't render anything when modal is closed
  if (!isOpen) return null;

  // --- Determine which modal to show ---

  // For the AI path: if we're in "processing" but have generatedText, show content_review instead
  const effectiveStep =
    state.currentStep === 'processing' &&
    state.materialSource === 'ai_generated' &&
    state.generatedText &&
    !state.processingError
      ? 'content_review'
      : state.currentStep;

  const renderCurrentModal = () => {
    switch (effectiveStep) {
      case 'resource_selection':
        return (
          <ResourceSelectionModal
            onSelect={handleSelectResources}
            onClose={onClose}
            defaultLanguage={
              TRANSLATION_LANGUAGES.find((l) => l.code === state.translationLanguage)
                ? state.translationLanguage
                : undefined
            }
          />
        );

      case 'audio_upload':
        return (
          <AudioUploadModal
            initialFiles={state.collectedData.audioFiles}
            initialMergedUrl={state.collectedData.mergedAudioUrl}
            initialMergedDuration={state.collectedData.mergedAudioDuration}
            initialRanges={state.collectedData.audioRanges}
            onMergeFiles={handleMergeAudioFiles}
            onSubmit={handleAudioSubmit}
            onBack={prevStep}
            onClose={onClose}
          />
        );

      case 'english_text':
        return (
          <EnglishTextModal
            initialText={state.collectedData.englishText}
            onSubmit={handleEnglishSubmit}
            onBack={prevStep}
            onClose={onClose}
          />
        );

      case 'translation_text':
        return (
          <TranslationTextModal
            initialText={state.collectedData.translationText}
            languageCode={state.translationLanguage}
            onSubmit={handleTranslationSubmit}
            onBack={prevStep}
            onClose={onClose}
          />
        );

      case 'ai_settings':
        return (
          <AISettingsModal
            defaultLanguage={state.translationLanguage}
            onSubmit={handleAISettingsSubmit}
            onBack={prevStep}
            onClose={onClose}
          />
        );

      case 'processing':
        return (
          <ProcessingModal
            status={state.processingStatus}
            error={state.processingError}
          />
        );

      case 'content_review':
        return (
          <ContentReviewModal
            generatedText={state.generatedText || ''}
            onConfirm={handleContentConfirm}
            onBack={prevStep}
          />
        );

      case 'completed':
        // Flow is done - should not normally render
        return null;

      default:
        return null;
    }
  };

  return <div className="resource-modal-flow">{renderCurrentModal()}</div>;
};

export default ResourceModalFlow;
